import { useCallback } from 'react';
import { useHandlers } from '../../../../context/page-context';
import type { Setting } from '../../../../context/settings-context';
import { useUpdateSettings } from '../../../../context/settings-context';
import { buildOAuthRedirectUri, generateOAuthState, OAUTH } from '../../oauth';

export { buildOAuthRedirectUri, generateOAuthState, OAUTH };

/**
 * Persists a partner modal's settings payload, then closes the modal.
 *
 * Pass `onSuccess` to replace the close (e.g. Xero saves the form before handing off to the
 * provider's OAuth flow, so the redirect happens instead of the close).
 */
export function usePartnerModalSave(onClose: () => void) {
  const { mutateAsync, isPending } = useUpdateSettings();
  const { notificationHandler } = useHandlers();

  const save = useCallback(
    async (settings: Setting[], onSuccess?: () => void) => {
      try {
        await mutateAsync(settings);
        (onSuccess ?? onClose)();
      } catch {
        // Leave the modal open so the user can retry; the optimistic update is rolled back.
        notificationHandler.error();
      }
    },
    [mutateAsync, notificationHandler, onClose],
  );

  return { save, isSaving: isPending };
}
